import React, { useState } from "react";
import { fmtCurrency, fmtPct, fmtNum, signColor } from "@/lib/format";
import { Trash2, Check, X } from "lucide-react";

export default function HoldingsTable({ holdings, onDelete }) {
  const [confirmId, setConfirmId] = useState(null);

  if (!holdings?.length) {
    return (
      <div className="card text-center py-12" data-testid="holdings-empty-state">
        <p className="font-serif text-2xl mb-2">No holdings yet</p>
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          Add your first position to begin tracking projected income.
        </p>
      </div>
    );
  }

  return (
    <div className="card p-0 overflow-x-auto">
      <table className="dt" data-testid="holdings-table">
        <thead>
          <tr>
            <th>Ticker</th>
            <th>Name</th>
            <th className="num">Shares</th>
            <th className="num">Cost / sh</th>
            <th className="num">Price</th>
            <th className="num">Market value</th>
            <th className="num">Gain/Loss</th>
            <th className="num">Yield</th>
            <th className="num">Annual income</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((h) => (
            <tr key={h.id} data-testid={`holding-row-${h.ticker}`}>
              <td>
                <span className="font-semibold">{h.ticker}</span>
                {h.quote_error && (
                  <span className="ml-2 text-[10px]" style={{ color: "var(--loss)" }} title={h.quote_error}>
                    !
                  </span>
                )}
              </td>
              <td style={{ color: "var(--text-secondary)" }} className="max-w-[200px] truncate">
                {h.name || "—"}
              </td>
              <td className="num">{fmtNum(h.shares, 4)}</td>
              <td className="num">{fmtCurrency(h.cost_basis)}</td>
              <td className="num">{h.price ? fmtCurrency(h.price) : "—"}</td>
              <td className="num">{fmtCurrency(h.market_value)}</td>
              <td className="num" style={{ color: signColor(h.gain_loss) }}>
                {h.gain_loss >= 0 ? "+" : ""}{fmtCurrency(h.gain_loss)}
                <div className="text-[10px] opacity-80">
                  {h.gain_loss_pct >= 0 ? "+" : ""}{fmtPct(h.gain_loss_pct)}
                </div>
              </td>
              <td className="num">
                {h.dividend_yield != null ? fmtPct(h.dividend_yield * 100) : "—"}
              </td>
              <td className="num" style={{ color: "var(--gain)" }}>
                {h.annual_income ? fmtCurrency(h.annual_income) : "—"}
              </td>
              <td className="num">
                {confirmId === h.id ? (
                  <div className="inline-flex items-center gap-1">
                    <button
                      data-testid={`confirm-delete-${h.ticker}`}
                      className="btn-ghost"
                      style={{ color: "var(--loss)" }}
                      onClick={() => { setConfirmId(null); onDelete(h.id, h.ticker); }}
                      title={`Confirm remove ${h.ticker}`}
                    >
                      <Check size={14} />
                    </button>
                    <button
                      data-testid={`cancel-delete-${h.ticker}`}
                      className="btn-ghost"
                      onClick={() => setConfirmId(null)}
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    data-testid={`delete-holding-${h.ticker}`}
                    className="btn-ghost"
                    onClick={() => setConfirmId(h.id)}
                    title={`Remove ${h.ticker}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
